import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, Textarea, VStack } from '@chakra-ui/react'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { createProjectChangeOrder, updateProjectChangeOrder } from 'api'
import { getAssetUrl, QUOTE_ADDITIONAL_ITEM_CATEGORIES } from 'lib'
import { MdDeleteOutline } from 'react-icons/md'

const initialForm = {
  name: '',
  company: '',
  contact: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  lineItems: []
}

export const ModalProjectChangeOrderFormType = {
  Add: 'Add',
  Edit: 'Edit',
  Approve: 'Approve',
  Approved: 'Approved',
  Delete: 'Delete'
}

export const ModalProjectChangeOrderForm = ({ isOpen, onClose, type = ModalProjectChangeOrderFormType.Add, changeOrder, nextChangeOrderName, project }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isLocked, setIsLocked] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setIsLocked(true)
  }, [isOpen])

  useEffect(() => {
    if (type === ModalProjectChangeOrderFormType.Add || !changeOrder) {
      setForm({
        ...initialForm,
        name: nextChangeOrderName,
        company: project?.customerCompany || '',
        contact: project?.customerName || '',
        address: project?.customerAddress || '',
        city: project?.customerCity || '',
        state: project?.customerState || '',
        zip: project?.customerZip || ''
      })
    } else {
      setForm(draft => {
        draft.name = changeOrder.name || ''
        draft.company = changeOrder.company || ''
        draft.contact = changeOrder.contact || ''
        draft.address = changeOrder.address || ''
        draft.city = changeOrder.city || ''
        draft.state = changeOrder.state || ''
        draft.zip = changeOrder.zip || ''
        draft.lineItems = changeOrder.lineItems ? JSON.parse(changeOrder.lineItems) : []
      })
    }
  }, [type, changeOrder, nextChangeOrderName, project, isOpen])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const { lineItems, ...rest } = form
      const price = lineItems.reduce((accumulator, currentValue) => {
        return accumulator + parseFloat(currentValue.price)
      }, 0)
      const commissionablePrice = lineItems.filter(
        item => item.category === QUOTE_ADDITIONAL_ITEM_CATEGORIES.EMC || item.category === QUOTE_ADDITIONAL_ITEM_CATEGORIES.Fabrication
      ).reduce((accumulator, currentValue) => {
        return accumulator + parseFloat(currentValue.price)
      }, 0)

      let response
      if (type === ModalProjectChangeOrderFormType.Add) {
        response = await createProjectChangeOrder({ ...rest, lineItems: JSON.stringify(lineItems), price, commissionablePrice, projectId: project?.id })
      } else {
        response = await updateProjectChangeOrder(changeOrder.id, { ...rest, lineItems: JSON.stringify(lineItems), price, commissionablePrice })
      }
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Change order saved.')
      setIsSaving(false)
      onClose()
      setForm({
        ...initialForm,
        company: project?.customerCompany || '',
        contact: project?.customerName || '',
        address: project?.customerAddress || '',
        city: project?.customerCity || '',
        state: project?.customerState || '',
        zip: project?.customerZip || ''
      })
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', 'Failed to save change order.')
    }
  }

  const handleChange = (e) => {
    setForm(draft => { draft[e.target.name] = e.target.value })
  }

  const handleAddLineItem = () => {
    const nextLineItemId = form.lineItems.reduce((accumulator, currentValue) => {
      if (currentValue.id > accumulator) {
        return currentValue.id
      } else {
        return accumulator
      }
    }, 0) + 1

    setForm(draft => {
      draft.lineItems = [
        ...form.lineItems,
        {
          id: nextLineItemId,
          item: '',
          category: '',
          price: 0
        }
      ]
    })
  }

  const handleChangeLineItem = (itemId, field, value) => {
    setForm(draft => {
      draft.lineItems = form.lineItems.map(item => {
        if (item.id !== itemId) {
          return item
        } else {
          return { ...item, [field]: value }
        }
      })
    })
  }

  const handleRemoveLineItem = (itemId) => {
    setForm(draft => {
      draft.lineItems = form.lineItems.filter(item => item.id !== itemId)
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" zIndex="10">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Change Order</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack alignItems="none" gap={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Item Name</FormLabel>
                    <Input name="name" value={form.name} isReadOnly={true} />
                  </FormControl>
                </GridItem>
              </Grid>
              <Heading size="sm">Client</Heading>
              <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Company</FormLabel>
                    <Input type="text" name="company" value={form.company} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Contact</FormLabel>
                    <Input type="text" name="contact" value={form.contact} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Address</FormLabel>
                    <Input type="text" name="address" value={form.address} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input type="text" name="city" value={form.city} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input type="text" name="state" value={form.state} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Zip</FormLabel>
                    <Input type="text" name="zip" value={form.zip} onChange={handleChange} isReadOnly={isLocked} />
                  </FormControl>
                </GridItem>
              </Grid>
              <Flex justifyContent="flex-end" mt={4}>
                <Button size="sm" onClick={() => setIsLocked(!isLocked)}>{isLocked ? 'Unlock' : 'Lock'} Inputs</Button>
              </Flex>
              <Heading size="sm">Line Item(s)</Heading>
              <VStack alignItems="none" gap={2}>
                {!form.lineItems.length && (
                  <Text fontSize="sm">No line items.</Text>
                )}
                {form.lineItems.map(item => {
                  return (
                    <Box key={item.id}>
                      <FormControl isRequired>
                        <FormLabel>Item Description</FormLabel>
                        <Textarea name="description" value={item.item} onChange={(e) => handleChangeLineItem(item.id, 'item', e.target.value)} rows="3" />
                      </FormControl>
                      <Flex justifyContent="space-between" gap={4} alignItems="flex-end" mt={2}>
                        <FormControl isRequired>
                          <FormLabel>Category</FormLabel>
                          <Select name="category" value={item.category} onChange={(e) => handleChangeLineItem(item.id, 'category', e.target.value)} placeholder="Select">
                            {Object.keys(QUOTE_ADDITIONAL_ITEM_CATEGORIES).map(v => {
                              return (
                                <option key={QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]} value={QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]}>{QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]}</option>
                              )
                            })}
                          </Select>
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel>Price</FormLabel>
                          <Input
                            type="number"
                            value={item.price}
                            onChange={e => handleChangeLineItem(item.id, 'price', e.target.value)}
                            onBlur={e => handleChangeLineItem(item.id, 'price', parseFloat(e.target.value || 0).toFixed(2))}
                          />
                        </FormControl>
                        <Button p={0} variant="ghost" onClick={() => handleRemoveLineItem(item.id)}>
                          <MdDeleteOutline fontSize="18px" />
                        </Button>
                      </Flex>
                    </Box>
                  )
                })}
              </VStack>
              <Button size="sm" w="fit-content" onClick={handleAddLineItem}>Add Item</Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={4}>
              <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                Save
              </Button>
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalProjectChangeOrderForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectChangeOrderFormType)),
  changeOrder: PropTypes.object,
  nextChangeOrderName: PropTypes.string,
  project: PropTypes.object
}
